import { DEV_MODE } from '../../global/env';
import { deferredPromise } from '../../utils/promise';
import { Logger } from '../logger';
export class RequestQueue {
    constructor(_host, _options = {}) {
        var _a;
        this._options = _options;
        this._requestQueue = new Map();
        this._pendingFlush = deferredPromise();
        /**
         * Whether callbacks should be called immediately or queued and flushed at a later time.
         */
        this.serveImmediately = false;
        /* c8 ignore start */
        if (DEV_MODE && _host && _options.name) {
            const className = _options.owner ? ` [${this.constructor.name}]` : '';
            this._logger = new Logger(_host, {
                owner: (_a = _options.owner) !== null && _a !== void 0 ? _a : this,
                name: `️⌛ ${this.name}${className}`
            });
        }
        /* c8 ignore stop */
    }
    get name() {
        return this._options.name;
    }
    /**
     * The number of callbacks that are currently in queue.
     */
    get size() {
        return this._requestQueue.size;
    }
    /**
     * Returns a clone of the current request queue.
     */
    cloneQueue() {
        return new Map(this._requestQueue);
    }
    /**
     * Waits for the queue to be flushed.
     */
    async waitForFlush() {
        if (this.serveImmediately)
            return;
        await this._pendingFlush.promise;
    }
    async queue(key, callback) {
        var _a;
        this._requestQueue.set(key, callback);
        /* c8 ignore start */
        if (DEV_MODE && this.name && !this.serveImmediately) {
            (_a = this._logger) === null || _a === void 0 ? void 0 : _a.debug(`queued \`${String(key)}\``);
        }
        /* c8 ignore stop */
        if (!this.serveImmediately)
            return;
        this.serve(key);
    }
    async serve(key) {
        var _a, _b;
        /* c8 ignore start */
        if (DEV_MODE && this.name) {
            (_a = this._logger) === null || _a === void 0 ? void 0 : _a.debug(`serving \`${String(key)}\``);
        }
        /* c8 ignore stop */
        await ((_b = this._requestQueue.get(key)) === null || _b === void 0 ? void 0 : _b());
        this._requestQueue.delete(key);
    }
    async flush() {
        var _a;
        const requests = Array.from(this._requestQueue.keys());
        await Promise.all(requests.map((reqKey) => this.serve(reqKey)));
        this._flush();
        /* c8 ignore start */
        if (DEV_MODE && this.name) {
            (_a = this._logger) === null || _a === void 0 ? void 0 : _a.info('flush');
        }
        /* c8 ignore stop */
    }
    _flush() {
        this._requestQueue.clear();
        // Release anyone waiting.
        this._pendingFlush.resolve();
        this._pendingFlush = deferredPromise();
    }
    reset() {
        var _a;
        this._flush();
        /* c8 ignore start */
        if (DEV_MODE && this.name) {
            (_a = this._logger) === null || _a === void 0 ? void 0 : _a.info('reset');
        }
        /* c8 ignore stop */
    }
    destroy() {
        var _a;
        this.serveImmediately = false;
        this.reset();
        /* c8 ignore start */
        if (DEV_MODE && this.name) {
            (_a = this._logger) === null || _a === void 0 ? void 0 : _a.info('destroy');
        }
        /* c8 ignore stop */
    }
}
